import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import AIAssessmentView from './AIAssessmentView';
import './StatusControl.css';
import './ApplicationReview.css';

const DOCUMENT_LABELS = {
  government_id: 'Government ID',
  employment_letter: 'Letter of Employment',
  paystub: 'Paystub',
  bank_statement: 'Bank Statement',
  reference_letter: 'Reference Letter',
  credit_report: 'Credit Report'
};

function ApplicationReview() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State hooks
  const [application, setApplication] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');

  const [unitLabel, setUnitLabel] = useState('');
  const [tenantRole, setTenantRole] = useState('primary');
  const [, setScreening] = useState(null);
  const [tuReport, setTuReport] = useState(null);
  const [, setTuReportLoading] = useState(true);
  const [tuPurchasing, setTuPurchasing] = useState(false);
  const [docsExpanded, setDocsExpanded] = useState(false);

  const [subscription, setSubscription] = useState(null);
  const [tuAddonChecked, setTuAddonChecked] = useState(false);
  const [aiAssessment, setAiAssessment] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState(null);
  
  // Lease state (minimal — full UI is in LeaseSigning.js)
  const [leaseDocs, setLeaseDocs] = useState([]);
  const [esignRequest, setEsignRequest] = useState(null);

  const [aiInsights, setAiInsights] = useState(null);
  const [, setAiInsightsLoading] = useState(true);
  const [, setAiInsightsError] = useState('');
  const [, setDocAiStatus] = useState({});
  const [aiReviewPending, setAiReviewPending] = useState(false);

  const [showCreditPopover, setShowCreditPopover] = useState(false);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [creditCheckoutLoading, setCreditCheckoutLoading] = useState(false);
  const [creditCheckoutError, setCreditCheckoutError] = useState('');
  const [aiCreditConfig, setAiCreditConfig] = useState(null);


  const commentInputRef = useRef(null);
  const aiPollingRef = useRef(null);
  const aiReviewBaselineRef = useRef(null);
  const aiSectionRef = useRef(null);

  useEffect(() => {
    console.log('[ApplicationReview] mounted', new Date().toISOString());
    return () => {
      console.log('[ApplicationReview] unmounted', new Date().toISOString());
    };
  }, []);

  useEffect(() => {
    console.log('[AI Review] pending', aiReviewPending, new Date().toISOString());
  }, [aiReviewPending]);

  const refreshAiInsights = useCallback(async (options = {}) => {
    const silent = typeof options === 'boolean' ? options : Boolean(options.silent);
    if (!silent) {
      setAiInsightsLoading(true);
      setAiInsightsError('');
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/ai-insights`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        throw new Error('AI insights are unavailable right now.');
      }
      const data = await res.json();
      setAiInsights(data);
      if (!data?.available) {
        setAiInsightsError('Use AI credits to analyze documents when they are uploaded, or continue with a manual review.');
      } else {
        setAiInsightsError('');
      }
      return data;
    } catch (error) {
      setAiInsightsError(error.message || 'AI insights are unavailable right now.');
      return null;
    } finally {
      if (!silent) {
        setAiInsightsLoading(false);
      }
    }
  }, [id]);

  const fetchAiCreditConfig = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/ai-credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.credits) {
        setAiCreditConfig(data);
      }
      return data;
    } catch (error) {
      return null;
    }
  }, []);

  const fetchDossier = useCallback(async (options = {}) => {
    const silent = typeof options === 'boolean' ? options : Boolean(options.silent);
    if (!silent) {
      setLoading(true);
    }
    const aiResult = await refreshAiInsights({ silent: true });
    const token = localStorage.getItem('token');
    try {
      const [appRes, commentsRes, screeningRes, leaseRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/screening`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/lease`, {
          headers: { Authorization: `Bearer ${token}` }
        }),

      ]);
      if (!appRes.ok) throw new Error('Could not fetch application');
      if (!commentsRes.ok) throw new Error('Could not fetch comments');
      const appData = await appRes.json();
      const commentsData = await commentsRes.json();
      const screeningData = screeningRes.ok ? await screeningRes.json() : { screening: null };
      const leaseData = leaseRes.ok ? await leaseRes.json() : { leases: [] };

      if (leaseData.leases && leaseData.leases.length > 0) {
        try {
          const esignRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/leases/${leaseData.leases[0].id}/esign`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (esignRes.ok) {
            const esignData = await esignRes.json();
            setEsignRequest(esignData.request || null);
          }
        } catch (err) {
          // ignore e-sign fetch errors
        }
      }

      const nextStatus = appData.application.status === 'info_needed'
        ? 'in_review'
        : (appData.application.status === 'lease_drafted_landlord_signed' ? 'approved' : appData.application.status);
      const nextApp = {
        ...appData.application,
        status: nextStatus
      };
      setApplication(nextApp);

      const nextDocs = nextApp.documents || [];
      if (nextDocs.length) {
        setDocAiStatus((prev) => {
          let changed = false;
          const next = { ...prev };
          nextDocs.forEach((doc) => {
            const docId = doc?.documentId ?? doc?.id;
            if (docId == null) return;
            if (doc.ai_status === 'completed' && !aiReviewPending && next[docId]?.state !== 'success') {
              next[docId] = { state: 'success', message: 'AI review updated' };
              changed = true;
            }
            if (doc.ai_status === 'retry' && next[docId]?.state !== 'error') {
              next[docId] = { state: 'error', message: 'AI review failed. Please retry.' };
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }

      setComments((commentsData.comments || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setScreening(screeningData.screening || null);
      const rawLeases = leaseData.leases || [];
      const sortedLeases = rawLeases.slice().sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
      setLeaseDocs(sortedLeases.slice(0, 1));
      setSelectedStatus(nextApp.status);


    } catch (err) {
      setError(err.message);
      setAiInsightsError('Failed to load AI insights.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
      setAiInsightsLoading(false);
    }
    return aiResult;
  }, [id, refreshAiInsights, aiReviewPending]);

  // Initial fetch on load or id change
  useEffect(() => {
    fetchDossier();
  }, [fetchDossier]);

  // Fetch TU report + subscription status + existing AI assessment
  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    if (!token) { setTuReportLoading(false); return; }
    Promise.all([
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tu-reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : { report: null }),
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null),
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/ai-assessment`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : { assessment: null })
    ])
      .then(([tuData, subData, assessData]) => {
        setTuReport(tuData?.report || null);
        setSubscription(subData);
        if (assessData?.assessment) {
          setAiAssessment(assessData.assessment);

        }
      })
      .catch(() => {})
      .finally(() => setTuReportLoading(false));
  }, [id]);

  // Handle Stripe TU purchase return - auto-run assessment
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tu_purchase') === 'success' && !aiAssessment) {
      // TU was just purchased; re-fetch TU report then trigger assessment
      const token = localStorage.getItem('token');
      if (!token || !id) return;
      const refetchAndAssess = async () => {
        try {
          const tuRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tu-reports/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const tuData = await tuRes.json();
          if (tuData?.report) setTuReport(tuData.report);
          // Now run assessment
          setAssessmentLoading(true);
          setAssessmentError(null);
          const assessRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/ai-assessment`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          });
          const assessData = await assessRes.json();
          if (!assessRes.ok) {
            setAssessmentError(assessData.error || 'Failed to run assessment');
          } else {
            setAiAssessment(assessData.assessment);
  
          }
        } catch (err) {
          setAssessmentError(err.message);
        } finally {
          setAssessmentLoading(false);
        }
        // Clean URL
        const url = new URL(window.location.href);
        url.searchParams.delete('tu_purchase');
        window.history.replaceState({}, '', url.toString());
      };
      refetchAndAssess();
    }
  }, [id, aiAssessment]);

  useEffect(() => {
    fetchAiCreditConfig();
  }, [fetchAiCreditConfig]);

  useEffect(() => {
    if (!aiReviewPending) {
      if (aiPollingRef.current) {
        clearInterval(aiPollingRef.current);
        aiPollingRef.current = null;
      }
      return;
    }
    const poll = async () => {
      const result = await refreshAiInsights({ silent: true });
      if (!result) return;
      const baseline = aiReviewBaselineRef.current;
      const nextSignature = getAiSummarySignature(result);
      if (!baseline && nextSignature) {
        aiReviewBaselineRef.current = nextSignature;
        return;
      }
      if (nextSignature && nextSignature !== baseline) {
        aiReviewBaselineRef.current = null;
        setAiReviewPending(false);
        // Single background re-fetch to update all cards/summary
        fetchDossier({ silent: true });
      }
    };
    poll();
    aiPollingRef.current = setInterval(poll, 5000);
    return () => {
      if (aiPollingRef.current) {
        clearInterval(aiPollingRef.current);
        aiPollingRef.current = null;
      }
    };
  }, [aiReviewPending, fetchDossier, refreshAiInsights]);

  useEffect(() => {
    if (aiReviewPending || !aiInsights?.available) return;
    setDocAiStatus((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(next).forEach(([docId, status]) => {
        if (status?.state === 'queued') {
          next[docId] = { state: 'success', message: 'AI review updated' };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [aiReviewPending, aiInsights]);

  // Sync selectedStatus with application.status
  useEffect(() => {
    if (application) {
      setSelectedStatus(application.status);
      setError(null);
      setUnitLabel('');
      setTenantRole('primary');
    }
  }, [application]);



  // Post a new comment
  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ comment_text: newComment, origin: 'application' })
      });
      if (!res.ok) throw new Error('Failed to post comment');
      const data = await res.json();
      setComments(prev => [data.comment, ...prev]);
      setNewComment('');
    } catch (err) {
      setError(err.message);
    }
  };

  // Request document from tenant
  const handleRequestCategoryDoc = async (categoryKey, categoryLabel, message) => {
    const token = localStorage.getItem('token');
    if (!token || !id || !message) return;
    try {
      // Post as a comment
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          comment_text: message,
          origin: 'document_request',
          metadata: { category: categoryKey, categoryLabel }
        })
      });
      if (!res.ok) throw new Error('Failed to send document request');
      const data = await res.json();
      setComments(prev => [data.comment, ...prev]);

      // Update status to docs_requested (bypass modal since message already sent via comment)
      await handleStatusChange('docs_requested', { source: 'document_request', silent: true, _docRequestConfirmed: true, doc_request_note: message });
    } catch (err) {
      setError(err.message);
    }
  };

  // Download document
  const handlePurchaseTuReport = async () => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    setTuPurchasing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tu-reports/purchase/${id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Unable to start checkout');
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err.message || 'Unable to purchase TU report');
    } finally {
      setTuPurchasing(false);
    }
  };

  const handleRunAssessment = async (force = false) => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    setAssessmentLoading(true);
    setAssessmentError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/ai-assessment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(force ? { force: true } : {})
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'INSUFFICIENT_CREDITS') {
          setShowCreditPurchaseModal(true);
          return;
        }
        throw new Error(data.error || data.message || 'Failed to run assessment');
      }
      setAiAssessment(data.assessment);
      setTimeout(() => aiSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      setAssessmentError(err.message);
    } finally {
      setAssessmentLoading(false);
    }
  };

  // Poll for assessment completion when pending
  const [pipelineStep, setPipelineStep] = useState(null);
  useEffect(() => {
    if (!aiAssessment || aiAssessment.status !== 'pending') { setPipelineStep(null); return; }
    setPipelineStep(aiAssessment.pipeline_step || 'queued');
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/ai-assessment`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data?.assessment) {
          setPipelineStep(data.assessment.pipeline_step || 'queued');
          if (data.assessment.status === 'completed') {
            setAiAssessment(data.assessment);
            setPipelineStep(null);
            clearInterval(interval);
            // Refresh insights (summaries, section data) after pipeline completes
            refreshAiInsights({ silent: true });
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [aiAssessment, id, refreshAiInsights]);

  // Update application status with confirmation and refresh dossier
  const [docRequestModal, setDocRequestModal] = useState(null);

  const handleStatusChange = useCallback(async (newStatus, options = { source: 'manual' }) => {
    if (newStatus === selectedStatus) return;

    if (newStatus === 'approved') {
      if (!window.confirm('Approve this application? You can then proceed to Lease & Signing.')) return;
    } else if (newStatus === 'rejected') {
      if (!window.confirm('Reject this application?')) return;
    }

    // Per-section document request — show picker
    if (newStatus === 'docs_requested' && !options._docRequestConfirmed) {
      setDocRequestModal({ source: options.source || 'manual' });
      return;
    }

    if (newStatus === 'approved') {
      if (!tenantRole) {
        setError('Tenant role is required to approve an application.');
        return;
      }

    }

    setSelectedStatus(newStatus);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/applications/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          status: newStatus,
          unit_label: newStatus === 'approved' ? unitLabel : undefined,
          role: newStatus === 'approved' ? tenantRole : undefined,
          silent: options.silent || false,
          requested_doc_types: options.requested_doc_types || undefined,
          doc_request_note: options.doc_request_note || undefined
        })
      });
      const resData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resData.error || 'Failed to update status');
      }
      if (newStatus === 'completed' && resData.active_tenant_id) {
        navigate(`/landlord/my-tenants/${resData.active_tenant_id}`);
        return;
      }
      await fetchDossier();
      // Scroll to lease CTA after approval
      if (newStatus === 'approved') {
        setTimeout(() => {
          const leaseSection = document.getElementById('review-lease');
          if (leaseSection) leaseSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    } catch (err) {
      setError(err.message);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDossier, id, selectedStatus, tenantRole, unitLabel, navigate]);



  const esignStatus = esignRequest?.status || '';
  const hasSignedLease = ['tenant_signed', 'completed'].includes(esignStatus) || (leaseDocs || []).some((doc) => ['tenant_signed', 'signed', 'completed'].includes(doc.status));
  const landlordSigned = esignStatus === 'landlord_signed' || (leaseDocs || []).some((doc) => doc.status === 'landlord_signed');


  const SECTION_DOC_TYPES = {
    identity: [],
    income: ['employment_letter', 'paystub', 'bank_statement'],
    references: ['reference_letter'],
    credit: [] // Credit data comes from TU report, not uploaded docs
  };
  const AI_RISK_RANK = {
    low: 1,
    medium: 2,
    high: 3
  };
  const getDocumentsByTypes = (types = []) => {
    if (!application?.documents || !types.length) return [];
    return application.documents.filter((doc) => types.includes(doc.document_type));
  };
  const rankToLevel = (rank) => {
    if (rank >= 3) return 'high';
    if (rank === 2) return 'medium';
    if (rank === 1) return 'low';
    return '';
  };

  const buildAiSummaryText = (docs = [], summary, emptyText, options = {}) => {
    if (summary) return summary;
    if (!docs.length) return emptyText;
    const highestRank = docs.reduce((acc, doc) => {
      const level = (doc.riskLevel || doc.ai_risk_level || '').toLowerCase();
      return Math.max(acc, AI_RISK_RANK[level] || 0);
    }, 0);
    const label = highestRank === 3 ? 'high' : highestRank === 2 ? 'medium' : highestRank === 1 ? 'low' : '';
    const docCount = docs.length;
    let text = `AI scanned ${docCount} document${docCount > 1 ? 's' : ''}${label ? ` - highest risk ${formatRiskLabel(label)}` : ''}`;
    if (options.includeFlags) {
      const flagCount = docs.reduce((sum, doc) => sum + (doc.findings?.length || 0), 0);
      text += flagCount > 0 ? ` • ${flagCount} flag${flagCount > 1 ? 's' : ''}` : ' • no flags yet';
    }
    return text;
  };

  const getAiSummarySignature = (payload) => {
    if (!payload) return '';
    const sections = payload.sectionSummaries || {};
    const stamp = payload.summaryGeneratedAt || '';
    return `${JSON.stringify(sections)}|${payload.reviewerSummary || ''}|${payload.tenantSummary || ''}|${stamp}`;
  };
  const identityDocs = aiInsights?.identity?.docs || [];
  const referenceDocs = aiInsights?.references?.docs || [];
  const creditSummary = aiInsights?.credit?.summary || null;
  // Prefer structured TU report data when available, fall back to AI-scanned PDF data
  const identityDocUploads = getDocumentsByTypes(SECTION_DOC_TYPES.identity);
  const incomeDocUploads = getDocumentsByTypes(SECTION_DOC_TYPES.income);
  const referenceDocUploads = getDocumentsByTypes(SECTION_DOC_TYPES.references);
  const creditDocUploads = getDocumentsByTypes(SECTION_DOC_TYPES.credit);
  const identitySummaryText = identityDocs.length ? (() => {
    const doc = identityDocs[0];
    const docLabel = DOCUMENT_LABELS[doc.documentType] || doc.documentType || 'Identity document';
    const riskLabel = formatRiskLabel(doc.riskLevel) || 'Pending';
    const flagCount = doc.findings?.length || 0;
    const primaryFlag = doc.findings?.[0]?.title || doc.findings?.[0]?.rule_code || null;
    return `${docLabel} · Risk ${riskLabel}${flagCount ? ` · ${flagCount} flag${flagCount > 1 ? 's' : ''}${primaryFlag ? ` (${primaryFlag})` : ''}` : ' · no flags yet'}`;
  })() : buildAiSummaryText(
    identityDocs.length ? identityDocs : identityDocUploads,
    aiInsights?.identity?.summary,
    'Identity is verified via Stripe Identity (government ID + selfie match).',
    { includeFlags: true }
  );
  const incomeSummaryText = incomeDocUploads.length ? (() => {
    const labels = incomeDocUploads.map((doc) => DOCUMENT_LABELS[doc.document_type] || doc.document_type || 'Income doc');
    const names = labels.slice(0, 2).join(', ') + (labels.length > 2 ? ' +' + (labels.length - 2) : '');
    const highestRank = incomeDocUploads.reduce((acc, doc) => {
      const rank = AI_RISK_RANK[(doc.ai_risk_level || '').toLowerCase()] || 0;
      return Math.max(acc, rank);
    }, 0);
    const riskLabel = formatRiskLabel(rankToLevel(highestRank)) || 'Pending';
    return `${names} · Risk ${riskLabel}`;
  })() : buildAiSummaryText(
    incomeDocUploads,
    aiInsights?.income?.summary,
    'AI insights appear after income documents are uploaded and processed.',
    { includeFlags: false }
  );
  const referenceSummaryText = buildAiSummaryText(referenceDocs.length ? referenceDocs : referenceDocUploads, null, 'AI insights appear after a reference letter is uploaded and processed.', { includeFlags: true });
  const creditSummaryText = creditSummary
    ? `Credit score ${creditSummary.score ?? '-'} (${formatRiskLabel(creditSummary.risk) || 'Pending'}) · Tradelines ${creditSummary.tradelines != null ? creditSummary.tradelines : '-'} · Derogatories ${creditSummary.derogatories != null ? creditSummary.derogatories : '-'}`
    : tuReport?.status === 'completed'
      ? 'TransUnion report available — included in AI assessment.'
      : 'Purchase a TransUnion credit check to include credit data in the assessment.';
  const llmSummaries = aiInsights?.sectionSummaries || null;
  const preReviewGuidance = 'Documents are uploaded. Run AI review to generate insights, or continue with a manual review.';
  const ignoreSummaryPattern = useMemo(() => /no .*documents? uploaded/i, []);
  const resolveSectionSummary = (summary, docs) => {
    const trimmed = (summary || '').trim();
    if (!trimmed) return '';
    if (docs.length > 0 && ignoreSummaryPattern.test(trimmed)) return '';
    return trimmed;
  };
  const shouldShowPreReview = (docs, summary) => !aiReviewPending && docs.length > 0 && !summary;
  const identitySectionSummary = resolveSectionSummary(llmSummaries?.identity, identityDocUploads);
  const incomeSectionSummary = resolveSectionSummary(llmSummaries?.income, incomeDocUploads);
  const referenceSectionSummary = resolveSectionSummary(llmSummaries?.references, referenceDocUploads);
  const creditSectionSummary = resolveSectionSummary(llmSummaries?.credit, creditDocUploads);
  const identitySummaryFinal = aiReviewPending ? (
    <>
      <span className="inline-spinner" aria-hidden="true" /> AI insights pending…
    </>
  ) : shouldShowPreReview(identityDocUploads, identitySectionSummary)
    ? preReviewGuidance
    : identitySectionSummary || identitySummaryText || 'No issues found.';
  const incomeSummaryFinal = aiReviewPending ? (
    <>
      <span className="inline-spinner" aria-hidden="true" /> AI insights pending…
    </>
  ) : shouldShowPreReview(incomeDocUploads, incomeSectionSummary)
    ? preReviewGuidance
    : incomeSectionSummary || incomeSummaryText || 'No issues found.';
  const referenceSummaryFinal = aiReviewPending ? (
    <>
      <span className="inline-spinner" aria-hidden="true" /> AI insights pending…
    </>
  ) : shouldShowPreReview(referenceDocUploads, referenceSectionSummary)
    ? preReviewGuidance
    : referenceSectionSummary || referenceSummaryText || 'No issues found.';
  const creditSummaryFinal = aiReviewPending ? (
    <>
      <span className="inline-spinner" aria-hidden="true" /> AI insights pending…
    </>
  ) : shouldShowPreReview(creditDocUploads, creditSectionSummary)
    ? preReviewGuidance
    : creditSectionSummary || creditSummaryText || 'No issues found.';
  const sectionSummaryParts = [];
  if (identitySummaryFinal) sectionSummaryParts.push(identitySummaryFinal);
  if (incomeSummaryFinal) sectionSummaryParts.push(incomeSummaryFinal);
  if (referenceSummaryFinal) sectionSummaryParts.push(referenceSummaryFinal);
  if (creditSummaryFinal) sectionSummaryParts.push(creditSummaryFinal);
  const aiCredits = aiInsights?.credits ?? aiCreditConfig?.credits ?? { balance: 0, limit: 0, resetsOn: null };
  const showAiCredits = Boolean(aiInsights || aiCreditConfig?.credits);
  const creditPurchaseEnabled = aiCredits?.purchaseEnabled ?? true;
  const creditResetEnabled = aiCredits?.resetEnabled ?? true;
  const topupCredits = aiCredits?.topupCredits ?? 0;
  const topupPriceCents = aiCredits?.topupPriceCents ?? 0;
  const topupCurrency = aiCredits?.currency ?? 'usd';

  if (loading && !application) return <main className="App-main">Loading application dossier...</main>;
  if (!application) return <main className="App-main">Application not found.</main>;
  const hasLeaseDoc = (leaseDocs || []).length > 0;

  const leaseStatusLabel = esignStatus === 'completed'
    ? 'Signed (e-sign complete)'
    : esignStatus === 'tenant_signed'
      ? 'Tenant signed - ready for final approval'
      : esignStatus === 'landlord_signed'
        ? 'Landlord signed - waiting on tenant'
        : esignStatus === 'sent'
          ? 'Sent for e-sign'
          : hasLeaseDoc
            ? 'Lease generated (not signed)'
            : 'Waiting';

  function formatRiskLabel(level) {
    if (!level) return 'Pending';
    const label = String(level);
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  const formatResetDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const formatCurrency = (valueCents, currency = 'usd') => {
    const cents = Number(valueCents);
    if (!Number.isFinite(cents)) return '-';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: String(currency || 'usd').toUpperCase()
      }).format(cents / 100);
    } catch (err) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const handleStartCreditCheckout = async () => {
    setCreditCheckoutLoading(true);
    setCreditCheckoutError('');
    const token = localStorage.getItem('token');
    if (!token) {
      setCreditCheckoutLoading(false);
      setCreditCheckoutError('Please sign in again to purchase credits.');
      return;
    }
    const creditsToBuy = aiCreditConfig?.credits?.topupCredits || aiInsights?.credits?.topupCredits || 80;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/ai-credits/checkout`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          credits: creditsToBuy,
          successUrl: `${window.location.origin}/landlord/application/${id}?aiCredits=success`,
          cancelUrl: `${window.location.origin}/landlord/application/${id}?aiCredits=cancel`
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Unable to start checkout.');
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('Checkout is unavailable right now.');
    } catch (error) {
      setCreditCheckoutError(error.message || 'Checkout is unavailable right now.');
    } finally {
      setCreditCheckoutLoading(false);
    }
  };






  return (
    <main className="App-main application-review-page" onSubmit={(event) => event.preventDefault()}>

      {showCreditPurchaseModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3>Purchase AI credits</h3>
            <p>Top up <strong>{topupCredits || '-'}</strong> credits for <strong>{formatCurrency(topupPriceCents, topupCurrency)}</strong>.</p>
            <p className="status-text">Current balance: {aiCredits.balance} of {aiCredits.limit || '-'} credits.</p>
            {aiCredits.planLimit ? (
              <p className="status-text">Nightly reset target: {aiCredits.planLimit} credits.</p>
            ) : null}
            {!creditPurchaseEnabled && (
              <p className="status-text status-text--disclaimer">Purchases are paused by ops. Contact support@rentual.co if you need a manual credit pack.</p>
            )}
            {creditCheckoutError && (
              <p className="error-message" style={{ marginTop: '0.5rem' }}>{creditCheckoutError}</p>
            )}
            <div className="form-actions">
              <button
                className="primary-button"
                type="button"
                onClick={handleStartCreditCheckout}
                disabled={!creditPurchaseEnabled || creditCheckoutLoading}
              >
                {creditCheckoutLoading ? 'Starting checkout…' : 'Continue to checkout'}
              </button>
              <button className="secondary-button" type="button" onClick={() => setShowCreditPurchaseModal(false)}>Not now</button>
            </div>
            <p className="status-text" style={{ marginTop: '0.75rem' }}>AI is assistive only. Review source documents yourself; Rentual assumes no liability.</p>
          </div>
        </div>
      )}

      {showAiCredits && (
        <div className="ai-credit-pill-wrapper">
          <div className="ai-credit-pill-inner">
            <button type="button" className="ai-credit-pill" onClick={() => setShowCreditPopover((prev) => !prev)}>
              <span className="ai-credit-icon">AI</span>
              <span className="ai-credit-text">Credits: {aiCredits.balance}/{aiCredits.limit || '-'}</span>
            </button>
            {showCreditPopover && (
              <div className="ai-credit-popover">
                <p><strong>{aiCredits.balance}</strong> credits remaining of {aiCredits.limit || '-'}</p>
                <p className="status-text status-text--disclaimer">AI is just an aid. Review the source documents yourself-Rentual takes no liability for your decisions.</p>
                <p className="status-text">Resets {creditResetEnabled ? formatResetDate(aiCredits.resetsOn) : 'Paused by ops'}</p>
                {!creditResetEnabled && (
                  <p className="status-text status-text--disclaimer">Auto resets are paused by ops. Reach out to support if you need a manual top-up.</p>
                )}
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => {
                    setCreditCheckoutError('');
                    setShowCreditPurchaseModal(true);
                  }}
                  disabled={!creditPurchaseEnabled}
                  title={creditPurchaseEnabled ? 'Buy credits' : 'Purchases are currently disabled'}
                >
                  Buy top-up
                </button>
                <p className="status-text" style={{ marginTop: '0.35rem' }}>Need help? Email support@rentual.co.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lease details hidden for now */}

      <div className="ledger-header" style={{ marginTop: '1.5rem' }}>
        <div>
          <p className="eyebrow-tag">Tenant Application</p>
          <h2>Tenant Application</h2>
          <p>Structured, report-style view of your applicant.</p>
          {error && <p className="error-message" style={{ marginTop: '0.5rem' }}>{error}</p>}
        </div>
      </div>

      <div className="status-control" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <strong>{application.full_name}</strong>
          {application.identity_status === 'verified' && (
            <>
              <span className="report-chip report-chip--success" title="Identity verified via selfie + ID match">✓ ID Verified</span>
              {application.identity_meta?.mismatches?.length > 0 && (
                <span className="report-chip report-chip--danger" title="Name on ID doesn't match application">⚠ Name Mismatch</span>
              )}
            </>
          )}
          {application.identity_status === 'pending' && (
            <span className="report-chip report-chip--muted" title="Identity verification in progress">ID Pending</span>
          )}
          {(!application.identity_status || application.identity_status === 'not_started') && (
            <span className="report-chip report-chip--muted" title="Identity not yet verified">ID Not Verified</span>
          )}
          {application.identity_status === 'failed' && (
            <span className="report-chip report-chip--danger" title="Identity verification failed">ID Failed</span>
          )}
          <span style={{ color: 'var(--light-text-color)' }}>·</span> {application.property_address}
        </div>
      </div>

      {/* ── Identity Verification Details ── */}
      {application.identity_status === 'verified' && application.identity_meta && (
        <div className="dashboard-section" style={{ marginTop: '0.75rem', borderLeft: '4px solid #16a34a', padding: '0.75rem' }}>
          <div className="section-header" style={{ marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0, fontSize: '0.95rem' }}>🪪 Identity Verification</h4>
            <span className="report-chip report-chip--success" style={{ fontSize: '0.7rem' }}>Stripe Identity</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.88rem' }}>
            {application.identity_meta.verified_name && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--light-text-color)' }}>Name on ID:</span>
                <strong>{application.identity_meta.verified_name}</strong>
                {application.identity_meta.mismatches?.length > 0 && (
                  <span style={{ color: '#dc2626', fontSize: '0.82rem' }}>
                    ⚠ Application says "{application.full_name}"
                  </span>
                )}
              </div>
            )}
            {application.identity_meta.verified_dob && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--light-text-color)' }}>DOB on ID:</span>
                <span>{application.identity_meta.verified_dob}</span>
              </div>
            )}
            {application.identity_meta.verified_address && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <span style={{ color: 'var(--light-text-color)' }}>Address on ID:</span>
                <span>{application.identity_meta.verified_address}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {application.identity_status === 'verified' && application.identity_meta?.mismatches?.length > 0 && (
        <div className="dashboard-section" style={{ marginTop: '0.5rem', borderLeft: '4px solid #dc2626', background: '#fef2f2', padding: '0.75rem' }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#dc2626', fontSize: '0.9rem' }}>⚠ Identity Mismatch Detected</p>
          <p className="status-text" style={{ marginTop: '0.25rem' }}>
            The name on the applicant's government ID does not match what they entered on the application. Review carefully.
          </p>
          {application.identity_meta.mismatches.map((m, i) => (
            <div key={i} style={{ marginTop: '0.35rem', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--light-text-color)' }}>{m.field}:</span>{' '}
              <span>Application: <strong>{m.application}</strong></span>{' · '}
              <span>ID Document: <strong>{m.id_document}</strong></span>
            </div>
          ))}
        </div>
      )}

      {/* ── Enriched applicant summary card (always visible) ── */}
      {(() => {
        const tu = tuReport?.status === 'completed' ? tuReport.report_data : null;
        const tuScore = tu?.score?.value;
        const tuDti = tu?.incomeEstimator?.debtToIncomePercentage;
        const tuRti = tu?.incomeEstimator?.rentToIncomePercentage;

        // Risk chip logic: TU data + AI risk levels
        const aiHighRisk = aiInsights?.available && (() => {
          const sections = ['credit', 'identity', 'income', 'references'];
          return sections.some(s => {
            const docs = aiInsights?.[s]?.docs || [];
            return docs.some(d => (d.riskLevel || d.ai_risk_level || '').toLowerCase() === 'high');
          });
        })();
        const aiMediumRisk = aiInsights?.available && (() => {
          const sections = ['credit', 'identity', 'income', 'references'];
          return sections.some(s => {
            const docs = aiInsights?.[s]?.docs || [];
            return docs.some(d => (d.riskLevel || d.ai_risk_level || '').toLowerCase() === 'medium');
          });
        })();
        const tuHighRisk = tuScore != null && (tuScore < 550 || (tu?.collections?.count || 0) > 0 || (tu?.publicRecords?.bankruptcies || 0) > 0);
        const tuMediumRisk = tuScore != null && tuScore < 650 && !tuHighRisk;
        const hasAnyData = tu || aiInsights?.available;
        let riskChip = null;
        if (hasAnyData) {
          if (aiHighRisk || tuHighRisk) {
            riskChip = { label: 'Risk flagged', bg: '#FEF2F2', border: '#FECACA', color: '#DC2626' };
          } else if (aiMediumRisk || tuMediumRisk) {
            riskChip = { label: 'Review needed', bg: '#FFF7ED', border: '#FCE7B2', color: '#92400E' };
          } else {
            riskChip = { label: 'Looks good', bg: '#ECFDF3', border: '#BBF7D0', color: '#16A34A' };
          }
        }

        const DetailRow = ({ label, value }) => value ? (
          <div className="ar-detail-row">
            <span className="ar-detail-label">{label}</span>
            <span className="ar-detail-value">{value}</span>
          </div>
        ) : null;

        return (
          <div className="applicant-summary-strip ar-summary-card">
            {/* Risk status bar */}
            {riskChip && (
              <div className={`ar-risk-bar ar-risk-bar--${riskChip.label === 'Risk flagged' ? 'high' : riskChip.label === 'Review needed' ? 'medium' : 'low'}`}>
                <span className="ar-risk-bar-label">{riskChip.label}</span>
                {tuScore != null && (
                  <span className="ar-risk-bar-source">TransUnion verified</span>
                )}
              </div>
            )}

            {/* Applicant details */}
            <div className="ar-summary-body">
              <DetailRow label="Occupants" value={application.num_occupants ? `${application.num_occupants}` : null} />
              <DetailRow label="Pets" value={application.pet_info || null} />
              <DetailRow label="Employment" value={application.employment_status || application.employmentStatus || null} />
              <DetailRow label="Stated income" value={application.income ? `$${Number(application.income).toLocaleString()}/mo` : null} />
              <DetailRow label="Move-in" value={application.move_in_date ? new Date(application.move_in_date).toLocaleDateString('en-CA') : null} />

              {/* TU metrics — clean row */}
              {tu && (tuScore != null || tuDti != null || tuRti != null) && (
                <div className="ar-tu-metrics">
                  {tuScore != null && (
                    <div className="ar-tu-metric">
                      <div className={`ar-tu-metric-value ${tuScore >= 700 ? 'ar-metric--good' : tuScore >= 600 ? 'ar-metric--warn' : 'ar-metric--bad'}`}>{tuScore}</div>
                      <div className="ar-tu-metric-label">Credit Score</div>
                    </div>
                  )}
                  {tuDti != null && (
                    <div className="ar-tu-metric ar-tu-metric--bordered">
                      <div className={`ar-tu-metric-value ${tuDti <= 35 ? 'ar-metric--good' : tuDti <= 50 ? 'ar-metric--warn' : 'ar-metric--bad'}`}>{Math.round(tuDti)}%</div>
                      <div className="ar-tu-metric-label">Debt-to-Income</div>
                    </div>
                  )}
                  {tuRti != null && (
                    <div className="ar-tu-metric ar-tu-metric--bordered">
                      <div className={`ar-tu-metric-value ${tuRti <= 30 ? 'ar-metric--good' : tuRti <= 40 ? 'ar-metric--warn' : 'ar-metric--bad'}`}>{Math.round(tuRti)}%</div>
                      <div className="ar-tu-metric-label">Rent-to-Income</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}


      {/* ── Unified Review Layout ── */}
      <div style={{ marginTop: '1rem' }}>

        {/* Document upload checklist */}
        {(() => {
          const incomeDocs = getDocumentsByTypes(['employment_letter', 'paystub', 'bank_statement']);
          const refDocs = getDocumentsByTypes(['reference_letter']);
          const creditDocs = getDocumentsByTypes(['credit_report']);
          const hasTu = tuReport?.status === 'completed';
          const docChecklist = [
            { label: 'Identity Verification', done: application.identity_status === 'verified' },
            { label: 'Income / Employment', done: incomeDocs.length > 0 },
            { label: 'References', done: refDocs.length > 0, optional: true },
            { label: hasTu ? 'Credit (TransUnion)' : 'Credit Report', done: hasTu || creditDocs.length > 0, optional: true },
          ];
          const requiredDone = docChecklist.filter(d => !d.optional && d.done).length;
          const requiredTotal = docChecklist.filter(d => !d.optional).length;
          const allRequiredComplete = requiredDone === requiredTotal;

          return (
            <div className="ar-doc-checklist">
              <div className="ar-doc-checklist-header">
                <span className="ar-doc-checklist-title">
                  Documents {allRequiredComplete ? '✓' : `(${requiredDone}/${requiredTotal})`}
                </span>
                {allRequiredComplete && (
                  <span className="ar-doc-checklist-done">All received</span>
                )}
              </div>
              <div className="ar-doc-checklist-grid">
                {docChecklist.map((doc, i) => (
                  <div key={i} className={`ar-doc-checklist-item ${doc.done ? 'is-done' : ''}`}>
                    <span className="ar-doc-checklist-icon">{doc.done ? '✅' : '⬜'}</span>
                    <span>{doc.label}{doc.optional ? ' (optional)' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Submitted documents list (collapsed by default) */}
        {(application.documents || []).length > 0 && (
            <div className="dashboard-section" style={{ marginTop: '0.75rem', padding: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setDocsExpanded(!docsExpanded)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-color)' }}
              >
                <h4 style={{ margin: 0, fontSize: '0.9rem' }}>📄 Submitted Documents ({application.documents.length})</h4>
                <span style={{ fontSize: '0.75rem', color: 'var(--light-text-color)', transition: 'transform 0.2s', transform: docsExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
              </button>
              {docsExpanded && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.5rem' }}>
                  {application.documents.map((doc, i) => (
                    <div key={doc.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.4rem 0.5rem', background: 'var(--section-bg, rgba(0,0,0,0.05))', borderRadius: '6px', fontSize: '0.85rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                        <span style={{ fontSize: '0.75rem', padding: '0.15rem 0.4rem', borderRadius: '4px', background: doc.ai_risk_level === 'low' ? 'rgba(34,197,94,0.15)' : doc.ai_risk_level === 'medium' ? 'rgba(251,191,36,0.15)' : doc.ai_risk_level === 'high' ? 'rgba(220,38,38,0.15)' : 'rgba(148,163,184,0.1)', color: doc.ai_risk_level === 'low' ? '#16a34a' : doc.ai_risk_level === 'medium' ? '#d97706' : doc.ai_risk_level === 'high' ? '#dc2626' : 'var(--light-text-color)', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {DOCUMENT_LABELS[doc.document_type] || doc.document_type}
                        </span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--light-text-color)' }}>
                          {doc.original_filename}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="link-button"
                        style={{ fontSize: '0.78rem', whiteSpace: 'nowrap', color: 'var(--accent-color, #2563eb)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/documents/${doc.id}/view`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (!res.ok) throw new Error('Could not load document');
                            const data = await res.json();
                            if (data.url) window.open(data.url, '_blank');
                          } catch (err) {
                            alert(err.message || 'Could not view document');
                          }
                        }}
                      >
                        View ↗
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
        )}

        {/* Standalone TU purchase option */}
        {tuReport?.status !== 'completed' && (
          <div className="dashboard-section" style={{ marginTop: '0.75rem', padding: '0.75rem', borderLeft: '4px solid #2563eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem' }}>TransUnion Credit Report</p>
                <p className="status-text" style={{ margin: '0.15rem 0 0' }}>
                  {tuReport?.status === 'pending' ? 'Purchase in progress — not yet completed.' : 'Pull a credit report for this applicant.'}
                </p>
              </div>
              <button
                className="primary-button"
                style={{ fontSize: '0.82rem', padding: '0.4rem 0.75rem', whiteSpace: 'nowrap' }}
                disabled={tuPurchasing}
                onClick={handlePurchaseTuReport}
              >
                {tuPurchasing ? 'Redirecting...' : 'Purchase — $29.99 CAD'}
              </button>
            </div>
          </div>
        )}

        {/* AI Assessment CTA or result */}
        <div ref={aiSectionRef} />
        {aiAssessment && aiAssessment.status !== 'pending' ? (
          <div className="review-mode-enter">
            <AIAssessmentView
              assessment={{
                ...aiAssessment,
                // Prefer insights summary (always fresh) over assessment's reviewer_summary
                reviewer_summary: aiInsights?.reviewerSummary || aiAssessment.reviewer_summary || aiAssessment.reasoning,
              }}
              application={application}
              tuReport={tuReport}
              documents={application.documents || []}
              applicationStatus={application.status}
              aiCredits={aiCredits}
              onPurchaseCredits={handleStartCreditCheckout}
              onApprove={!['approved', 'completed', 'rejected'].includes(application.status) ? () => handleStatusChange('approved', { source: 'ai_assessment' }) : undefined}
              onDecline={!['approved', 'completed', 'rejected'].includes(application.status) ? () => handleStatusChange('rejected', { source: 'ai_assessment' }) : undefined}
              onUnapprove={application.status === 'approved' ? () => {
                if (!window.confirm('Move this application back to review? The tenant will be notified.')) return;
                handleStatusChange('in_review', { source: 'ai_assessment' });
              } : undefined}
              onRequestInfo={() => handleStatusChange('docs_requested', { source: 'ai_assessment' })}
              onPurchaseTu={handlePurchaseTuReport}
              onRerunAssessment={() => handleRunAssessment(true)}
              onRequestCategoryDoc={handleRequestCategoryDoc}
            />
          </div>
        ) : (assessmentLoading || pipelineStep || (aiAssessment && aiAssessment.status === 'pending')) ? (
          <div className="assessment-loading-card review-mode-enter">
            <h3>Running AI Assessment</h3>
            <p className="status-text">Analyzing documents and generating your assessment report.</p>
            <div className="assessment-loading-steps">
              {[
                { key: 'ocr', label: 'Scanning documents' },
                { key: 'fields', label: 'Extracting data' },
                { key: 'scoring', label: 'Analyzing risk' },
                { key: 'summaries', label: 'Generating summary' },
                { key: 'finalizing', label: 'Finalizing verdict' },
              ].map((step, i, arr) => {
                const stepOrder = arr.map(s => s.key);
                const currentIdx = stepOrder.indexOf(pipelineStep);
                const thisIdx = i;
                const isDone = pipelineStep === 'done' || (currentIdx >= 0 && thisIdx < currentIdx);
                const isActive = currentIdx === thisIdx;
                return (
                  <div key={step.key} className={`assessment-loading-step ${isDone ? 'done' : isActive ? 'active' : ''}`}>
                    <span className="step-icon">
                      {isDone ? '✓' : isActive ? <span className="inline-spinner" aria-hidden="true" /> : '○'}
                    </span>
                    <span>{step.label}{isActive ? '…' : ''}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="ar-ai-cta">
            <p className="ar-ai-cta-title">
              🤖 AI Assessment
            </p>
            <p className="ar-ai-cta-desc">
              Run a full AI analysis across all documents with cross-referencing and a unified verdict.
            </p>
            <button
              className="primary-button"
              type="button"
              onClick={handleRunAssessment}
              disabled={assessmentLoading || aiCredits.balance < 5}
              style={{ minWidth: '220px' }}
            >
              Run AI Assessment (5 credits)
            </button>
            {aiCredits.balance < 5 && (
              <p className="status-text" style={{ marginTop: '0.4rem', fontSize: '0.8rem' }}>
                {aiCredits.balance} credits remaining.{' '}
                {creditPurchaseEnabled && topupCredits > 0 && (
                  <button type="button" className="link-button" onClick={handleStartCreditCheckout}>
                    Buy {topupCredits} credits (${(topupPriceCents / 100).toFixed(2)} {topupCurrency.toUpperCase()})
                  </button>
                )}
              </p>
            )}
            {assessmentError && (
              <p className="error-message" style={{ marginTop: '0.5rem', fontSize: '0.82rem' }}>{assessmentError}</p>
            )}
            {/* TU add-on */}
            <div className="ar-tu-addon">
              {tuReport?.status === 'completed' ? (
                <span className="ar-tu-addon-included">✓ TransUnion report included</span>
              ) : subscription?.subscription_status === 'active' ? (
                <label className="ar-tu-addon-label">
                  <input type="checkbox" checked={tuAddonChecked} onChange={e => setTuAddonChecked(e.target.checked)} />
                  <span className="ar-tu-addon-text">Add TransUnion Report <span className="ar-tu-addon-price">(+$29.99)</span></span>
                </label>
              ) : null}
            </div>
          </div>
        )}

        {/* ── Category Sections (always visible as expandable cards) ── */}
        {/* Lease & Signing CTA */}
        {(application?.status === 'approved' || hasSignedLease || application?.status === 'lease_tenant_signed' || application?.status === 'completed') && (
        <div className="dossier-section ar-lease-section" id="review-lease">
          <div className="section-header" style={{ alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left' }}>
            <div><h3>Lease & Signing</h3></div>
          </div>
          <div className="ar-lease-row">
            <div className="ar-lease-info">
              <p className="ar-lease-status-title">
                {application?.status === 'completed' ? '🎉 Lease fully executed'
                  : hasSignedLease ? '✅ Tenant signed — ready to finalize'
                  : landlordSigned ? '⏳ Awaiting tenant signature'
                  : hasLeaseDoc ? '📄 Lease uploaded — send for signing'
                  : 'Upload or generate a lease'}
              </p>
              <p className="status-text" style={{ margin: 0 }}>{leaseStatusLabel}</p>
            </div>
            <Link to={`/landlord/applications/${id}/lease`} className="primary-button" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
              {application?.status === 'completed' ? 'View Lease' : 'Proceed to Lease & Signing →'}
            </Link>
          </div>
        </div>
        )}

        {/* Decision — only show if no AI assessment (AI view has its own buttons) */}
        {!aiAssessment && application.status !== 'approved' && application.status !== 'completed' && (
          <div className="ar-decision-bar">
            <button className="primary-button ar-decision-btn" type="button"
              onClick={() => handleStatusChange('approved')}>
              ✓ Approve
            </button>
            <button className="ghost-button ar-decision-btn ar-decision-btn--decline" type="button"
              onClick={() => {
                if (!window.confirm('Reject this application?')) return;
                handleStatusChange('rejected');
              }}>
              ✕ Decline
            </button>
          </div>
        )}
        {!aiAssessment && application.status === 'approved' && (
          <div className="ar-decision-bar" style={{ justifyContent: 'flex-end' }}>
            <button className="ghost-button" type="button" style={{ fontSize: '0.85rem' }}
              onClick={() => {
                if (!window.confirm('Move this application back to review? The tenant will be notified.')) return;
                handleStatusChange('in_review');
              }}>
              ← Move back to review
            </button>
          </div>
        )}

      </div>

      {/* Communication thread */}
      <div className="dossier-grid" style={{ marginTop: '1rem' }}>
        <div className="dossier-section comments-section">
          <h3>Communication Thread</h3>
          <div className="comment-list">
            {comments.map(c => (
              <div key={c.id} className={`comment ${c.user_type}`}>
                <p>{c.comment_text}</p>
                <small>By {c.user_type} · {new Date(c.created_at).toLocaleDateString('en-CA')} {new Date(c.created_at).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' })}</small>
              </div>
            ))}
          </div>
          <textarea className="comment-box" ref={commentInputRef} value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Type a message..." />
          <button onClick={handlePostComment} className="primary-button comment-send">Send</button>
        </div>
      </div>
      {/* Document request modal */}
      {docRequestModal && (() => {
        const docTypes = [
          { key: 'government_id', label: 'Government ID' },
          { key: 'employment_letter', label: 'Employment letter' },
          { key: 'paystub', label: 'Pay stubs' },
          { key: 'bank_statement', label: 'Bank statement' },
          { key: 'reference_letter', label: 'Reference letter' },
          { key: 'credit_report', label: 'Credit report' },
          { key: 'other', label: 'Other' }
        ];
        return (
          <div className="modal-backdrop" onClick={() => setDocRequestModal(null)}>
            <div className="modal-content ar-doc-modal" onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>Request documents</h3>
              <p className="status-text">Select which documents the tenant should upload:</p>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const selected = docTypes.filter(d => formData.get(d.key)).map(d => d.key);
                const note = formData.get('note') || '';
                setDocRequestModal(null);
                handleStatusChange('docs_requested', {
                  source: docRequestModal.source,
                  _docRequestConfirmed: true,
                  requested_doc_types: selected.length > 0 ? selected : undefined,
                  doc_request_note: note || undefined
                });
              }}>
                <div className="ar-doc-modal-checks">
                  {docTypes.map(d => (
                    <label key={d.key} className="ar-doc-modal-check-label">
                      <input type="checkbox" name={d.key} /> {d.label}
                    </label>
                  ))}
                </div>
                <textarea
                  name="note"
                  placeholder="Add a note for the tenant (optional)"
                  rows={2}
                  className="ar-doc-modal-note"
                />
                <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                  <button type="button" className="secondary-button" onClick={() => setDocRequestModal(null)}>Cancel</button>
                  <button type="submit" className="primary-button">Send request</button>
                </div>
              </form>
            </div>
          </div>
        );
      })()}
    </main>
  );
}

export default ApplicationReview;
